import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface FileWithSession {
  id: string;
  file_name: string;
  file_type: 'audio' | 'text' | 'document' | 'transcript';
  file_size: number;
  storage_path: string;
  created_at: string;
  uploaded_by_id: string;
  uploaded_by_name: string;
  session_id: string;
  session_date: string;
  aircraft_type: string;
  trainee_name: string;
}

export interface FileStats {
  audioCount: number;
  transcriptCount: number;
  totalBytes: number;
}

export interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

interface RawFileRow {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  created_at: string;
  uploaded_by: string | null;
  uploaded_by_user: { full_name: string } | { full_name: string }[] | null;
  session: {
    id: string;
    session_date: string;
    aircraft_type: string;
    trainee: { full_name: string } | { full_name: string }[] | null;
  } | null;
}

const SORT_COLUMN: Record<'date' | 'name' | 'size', string> = {
  date: 'created_at',
  name: 'file_name',
  size: 'file_size',
};

export function useFiles() {
  const { user } = useAuth();

  const [files, setFiles] = useState<FileWithSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<FileStats>({ audioCount: 0, transcriptCount: 0, totalBytes: 0 });
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FileWithSession | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const [searchTerm, setSearchTermRaw] = useState('');
  const [fileTypeFilter, setFileTypeFilterRaw] = useState('all');
  const [sortBy, setSortByRaw] = useState<'date' | 'name' | 'size'>('date');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const userRole = user?.user_metadata?.role || 'trainee';

  const showToast = useCallback((message: string, type: Toast['type'] = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const canDeleteFile = useCallback(
    (file: FileWithSession) => userRole === 'admin' || file.uploaded_by_id === user?.id,
    [userRole, user?.id]
  );

  // Setters that reset page to 1
  const setSearch = useCallback((v: string) => { setSearchTermRaw(v); setPage(1); }, []);
  const setTypeFilter = useCallback((v: string) => { setFileTypeFilterRaw(v); setPage(1); }, []);
  const setSortBy = useCallback((v: 'date' | 'name' | 'size') => { setSortByRaw(v); setPage(1); }, []);
  const setPageSize = useCallback((v: number) => { setPageSizeRaw(v); setPage(1); }, []);
  const clearFilters = useCallback(() => { setSearchTermRaw(''); setFileTypeFilterRaw('all'); setPage(1); }, []);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      if (!user) return;
      setLoading(true);
      try {
        // Short-circuit: if the user has no sessions, there can be no files to show.
        const { count: sessionCount, error: sessionError } = await supabase
          .from('sessions')
          .select('id', { count: 'exact', head: true })
          .abortSignal(controller.signal);
        if (sessionError) throw sessionError;
        if (controller.signal.aborted) return;
        if (sessionCount === 0) {
          setFiles([]);
          setTotalCount(0);
          setStats({ audioCount: 0, transcriptCount: 0, totalBytes: 0 });
          return;
        }

        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        // RLS (Migration 208) enforces role-based access server-side.
        let query = supabase
          .from('files')
          .select(
            `
            id,
            file_name,
            file_type,
            file_size,
            storage_path,
            created_at,
            uploaded_by,
            uploaded_by_user:uploaded_by(full_name),
            session:session_id(
              id,
              session_date,
              aircraft_type,
              trainee:trainee_id(full_name)
            )
          `,
            { count: 'exact' }
          )
          .abortSignal(controller.signal)
          .order(SORT_COLUMN[sortBy], { ascending: sortBy === 'name' })
          .range(from, to);

        if (fileTypeFilter !== 'all') query = query.eq('file_type', fileTypeFilter);
        if (searchTerm.trim()) query = query.ilike('file_name', `%${searchTerm.trim()}%`);

        const [{ data, error, count }, statsResult] = await Promise.all([
          query,
          supabase.rpc('get_file_stats').abortSignal(controller.signal).single(),
        ]);
        const statsData = statsResult.data as { audio_count: number; transcript_count: number; total_bytes: number } | null;
        const statsError = statsResult.error;

        if (error) throw error;
        if (statsError) throw statsError;
        if (controller.signal.aborted) return;

        setTotalCount(count ?? 0);
        if (statsData) {
          setStats({
            audioCount: Number(statsData.audio_count),
            transcriptCount: Number(statsData.transcript_count),
            totalBytes: Number(statsData.total_bytes),
          });
        }
        setFiles(
          (data as unknown as RawFileRow[] || []).map((f) => ({
            id: f.id,
            file_name: f.file_name,
            file_type: f.file_type as FileWithSession['file_type'],
            file_size: f.file_size,
            storage_path: f.storage_path,
            created_at: f.created_at,
            uploaded_by_id: f.uploaded_by || '',
            uploaded_by_name: Array.isArray(f.uploaded_by_user)
              ? f.uploaded_by_user[0]?.full_name || 'Unknown'
              : f.uploaded_by_user?.full_name || 'Unknown',
            session_id: f.session?.id || '',
            session_date: f.session?.session_date || '',
            aircraft_type: f.session?.aircraft_type || 'Unknown',
            trainee_name: Array.isArray(f.session?.trainee)
              ? f.session.trainee[0]?.full_name || 'Unknown'
              : f.session?.trainee?.full_name || 'Unknown',
          }))
        );
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (err && typeof err === 'object' && 'message' in err &&
            String((err as { message: unknown }).message).includes('AbortError')) return;
        console.error('Error loading files:', err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [user, page, pageSize, searchTerm, fileTypeFilter, sortBy, refreshTrigger]);

  const handleDownload = useCallback(async (file: FileWithSession) => {
    try {
      const { data, error } = await supabase.storage
        .from('session-files')
        .download(file.storage_path);
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      showToast('Failed to download file', 'error');
    }
  }, [showToast]);

  const handleDelete = useCallback((file: FileWithSession) => {
    if (!canDeleteFile(file)) {
      showToast('You do not have permission to delete this file.', 'error');
      return;
    }
    setPendingDelete(file);
  }, [canDeleteFile, showToast]);

  const executeDelete = useCallback(async () => {
    const file = pendingDelete;
    if (!file) return;
    setPendingDelete(null);
    setDeletingFileId(file.id);
    try {
      const { error: storageError } = await supabase.storage
        .from('session-files')
        .remove([file.storage_path]);
      if (storageError) throw storageError;

      const { error: dbError } = await supabase.from('files').delete().eq('id', file.id);
      if (dbError) throw dbError;

      setRefreshTrigger((n) => n + 1);
      showToast('File deleted successfully');
    } catch (err) {
      console.error('Error deleting file:', err);
      showToast('Failed to delete file', 'error');
    } finally {
      setDeletingFileId(null);
    }
  }, [pendingDelete, showToast]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  return {
    // data
    files,
    loading,
    totalCount,
    stats,
    toasts,
    // pagination
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    rangeStart,
    rangeEnd,
    // filters
    searchTerm,
    setSearch,
    fileTypeFilter,
    setTypeFilter,
    sortBy,
    setSortBy,
    clearFilters,
    // actions
    deletingFileId,
    pendingDelete,
    setPendingDelete,
    handleDownload,
    handleDelete,
    executeDelete,
    canDeleteFile,
  };
}
